import {format, isValid} from 'date-fns';
import {ClientProfile, EncounterType} from '@/api/dto/client-profile';
import {BasicUser, User} from "@/api/dto/dto";

// Flowable Workflow is a temporary event type until we get workflows (and event types) defined by product
// https://hbuco.atlassian.net/browse/CARECO-4605
export enum CareJourneyEventType {
  TCM_REFERRAL = 'TCM_REFERRAL',
  ADT_ER_BH = 'ADT_ER_BH',
  ADT_3YR_ER_BH = 'ADT_3YR_ER_BH',
  ADT_3YR_IP_BH = 'ADT_3YR_IP_BH',
  ADT_IP_BH_PLUS_PCP = 'ADT_IP_BH_PLUS_PCP',
  ADT_NONROSTER_IP_BH = 'ADT_NONROSTER_IP_BH',
  ADT_NONROSTER_ER_BH = 'ADT_NONROSTER_ER_BH',
  FLOWABLE_WORKFLOW = 'FLOWABLE_WORKFLOW',
  TCM_BH_REFERRAL = 'TCM_BH_REFERRAL',
  BH_SCHEDULE_APPOINTMENT = 'BH_SCHEDULE_APPOINTMENT',
  BH_APPOINTMENT = 'BH_APPOINTMENT'
}

export enum CareJourneyType {
  TCM = "TCM",
  TCM_BH_REFERRAL = "TCM_BH_REFERRAL",
  BH_JOURNEY = "BH_JOURNEY",
  BH_SCHEDULE_APPOINTMENT = "BH_SCHEDULE_APPOINTMENT"
}

export enum CareJourneyPriority {
  URGENT = 'URGENT',
}

export enum CareJourneyAttachmentType {
  USER_UPLOAD = 'USER_UPLOAD',
  DMCS_CARE_NOTE = 'DMCS_CARE_NOTE',
  CARE_TEAM_REFERRAL = 'CARE_TEAM_REFERRAL',
  OUTREACH_ATTEMPTS_SUMMARY = 'OUTREACH_ATTEMPTS_SUMMARY'
}

export enum CareJourneyFaxStatus {
  WAITING = 'WAITING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
}

export enum CareJourneyLogType {
  ASSIGNED = 'ASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
  STEP_STARTED = 'STEP_STARTED',
  STEP_COMPLETED = 'STEP_COMPLETED',
  STEP_FAILED = 'STEP_FAILED',
  STEP_SUBMITTED = 'STEP_SUBMITTED',
  CLOSED = 'CLOSED',
  DUE_DATE_CHANGED = 'DUE_DATE_CHANGED',
  PRIORITY_CHANGED = 'PRIORITY_CHANGED',
  FILE_DELIVERED = 'FILE_DELIVERED',
  CARE_TEAM_REFERRAL_SENT = 'CARE_TEAM_REFERRAL_SENT',
  CARE_NOTE_MANUALLY_SENT = 'CARE_NOTE_MANUALLY_SENT',
  UNKNOWN = 'UNKNOWN',
  APPOINTMENT_REBOOK_NEEDED = 'APPOINTMENT_REBOOK_NEEDED',
  APPOINTMENT_CREATED = 'APPOINTMENT_CREATED',
  APPOINTMENT_SEALED = 'APPOINTMENT_SEALED',
  APPOINTMENT_REOPENED = 'APPOINTMENT_REOPENED',
  APPOINTMENT_CANCELLED = 'APPOINTMENT_CANCELLED',
  CAMPAIGN_ENROLLED = 'CAMPAIGN_ENROLLED',
  CAMPAIGN_MANUALLY_REENROLLED = 'CAMPAIGN_MANUALLY_REENROLLED',
  CAMPAIGN_ENROLLMENT_FAILED = 'CAMPAIGN_ENROLLMENT_FAILED',
}

export enum TaskStepName {
  NONE = 'NONE',
  INITIAL_OUTREACH = 'initial_outreach',
  INTAKE = 'intake',
  SCHEDULED_APPOINTMENT = 'schedule_appointment',
  SCHEDULED_APPOINTMENTS = 'schedule_appointments',
  SMS_APPOINTMENT_NOTIFICATION = 'sms_appointment_notification',
  SMS_APPOINTMENT_REMINDER = 'sms_appointment_reminder',
  SMS_APPOINTMENT_CONFIRMATION = 'sms_appointment_confirmation',
  APPOINTMENT_NOTIFICATION = 'appointment_notification',
  APPOINTMENT_CONFIRMATION = 'appointment_confirmation',
  PRE_INITIAL_OUTREACH = 'pre_initial_outreach',
  REVIEW = 'review'
}

export enum DaysOpenValue {
  OneDay = '1_day',
  TwoToThreeDays = '2_3_days',
  FourToFiveDays = '4_5_days',
  SixToSevenDays = '6_7_days',
  MoreThanOneWeek = 'gt_1_week',
  MoreThanTwoWeeks = 'gt_2_weeks',
  MoreThanOneMonth = 'gt_1_month',
  MoreThanThreeMonths = 'gt_3_months',
  MoreThanSixMonths = 'gt_6_months',
  MoreThanOneYear = 'gt_1_year',
}

export enum AttestationStatus {
  NOT_READY = 'NOT_READY',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum DirectMessageTargetType {
  PROVIDER = 'PROVIDER',
  CARE_TEAM = 'CARE_TEAM',
}

export enum DirectMessageStatus {
  PENDING = 'PENDING',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
}

export const attestationStatusDisplayValues: Record<AttestationStatus, string> = {
  [AttestationStatus.NOT_READY]: 'Not ready',
  [AttestationStatus.PENDING]: 'Pending',
  [AttestationStatus.COMPLETED]: 'Completed',
  [AttestationStatus.NOT_REQUIRED]: 'Not required',
};

export const taskTypeDisplayValues: Record<CareJourneyEventType, string> = {
  [CareJourneyEventType.TCM_REFERRAL]: 'Transitional care management (TCM)',
  [CareJourneyEventType.TCM_BH_REFERRAL]: 'TCM_BH_REFERRAL',
  [CareJourneyEventType.ADT_ER_BH]: 'ADT_ER_BH',
  [CareJourneyEventType.ADT_3YR_ER_BH]: 'ADT_3YR_ER_BH',
  [CareJourneyEventType.ADT_3YR_IP_BH]: 'ADT_3YR_IP_BH',
  [CareJourneyEventType.ADT_IP_BH_PLUS_PCP]: 'ADT_IP_BH_PLUS_PCP',
  [CareJourneyEventType.ADT_NONROSTER_IP_BH]: 'ADT_NONROSTER_IP_BH',
  [CareJourneyEventType.ADT_NONROSTER_ER_BH]: 'ADT_NONROSTER_ER_BH',
  [CareJourneyEventType.FLOWABLE_WORKFLOW]: 'Flowable workflow',

  // Not user-facing event
  [CareJourneyEventType.BH_SCHEDULE_APPOINTMENT]: 'SCHEDULE_APPOINTMENT',
  [CareJourneyEventType.BH_APPOINTMENT]: 'BH_APPOINTMENT',
};

export const taskStatusDisplayValues : Record<string, string> = {
  active: 'In progress',
  closed: 'Closed',
  cancelled: 'Closed',
  completed: 'Completed',
  skipped: 'Skipped'
};

export const referralReviewStatusDisplayValues: Record<string, string> = {
  PENDING_REVIEW: 'Pending Review',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
};


export interface CareJourneyResponse {
  careJourneyInstanceId: string;
  rosterId?: number;
  journeySource: 'TCM' | 'FLOWABLE';
  triggeringEventType: EncounterType;
  triggeringEventDateTime: string;
  eventType: CareJourneyEventType;
  eventSetting: string | null;
  priority: CareJourneyPriority | null;
  lastCompletedStep: string | null;
  nextStep: string | null;
  lastViewedAt: string | null;
  dueDate: string;
  status: string | null;
  patientId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  userAssignment: CareJourneyUserAssignment;
  groupName: string;
  organizationName: string | null;
  // Populated only for FLOWABLE journeys (CARECO-6081): the workflow's display title and a
  // user-facing next-step label; null for TCM/OE journeys.
  workflowName: string | null;
  nextStepLabel: string | null;
  referralReviewMetadata: CareJourneyReferralReviewMetadata | null;
}

export interface CareJourneyReferralReviewMetadata {
  status: string
}

export interface CareJourneyResponses {
  searchTotal: number,
  rosterTotal: number,
  careJourneys: Array<CareJourneyResponse>
}

export enum JourneySource {
  TCM = 'TCM',
  FLOWABLE = 'FLOWABLE',
}

export enum JourneyInitiationMode {
  AI = 'AI',
  HUMAN_ONLY = 'HUMAN_ONLY',
  HITL = 'HITL',
}

export enum InitiationReason {
  HIGH_RISK = 'HIGH_RISK',
  NEEDS_EXCLUSION_REVIEW = 'NEEDS_EXCLUSION_REVIEW',
  CUSTOMER_DEFINED_HITL = 'CUSTOMER_DEFINED_HITL',
  CUSTOMER_DEFINED_HUMAN_ONLY = 'CUSTOMER_DEFINED_HUMAN_ONLY',
}

export interface OrchestrationEngineDetails {
  actor: string;
  unassignedByAi: boolean;
  reason: string | null;
  reasonStep: string | null;
  // Appointment sub-journeys only; mapped from metadata.reason in OE response
  closeReason?: string | null;
  appointmentSealed?: boolean;
  attestation?: Attestation;
  directMessageDeliveries: DirectMessageDelivery[];
  rawResponse?: Record<string, unknown>;
  initiationReasons: InitiationReason[];
  initiationMode: JourneyInitiationMode | null;
}

// Will be populated as CWE features ship (e.g., workflow metadata, form bindings)
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CweDetails {}

export interface EscalatedByUser {
  id: string;
  first_name: string;
  last_name: string;
}

export interface AdditionalEvent {
  referralId: string;
  dataType: string | null;
  rejectReason: string | null;
  eventDateTime: string | null;
  triggeringEncounterId: string | null;
  createdAt: string;
  noteThreadId: number | null;
}

export interface CareJourneyDetailsResponse {
  careJourneyInstanceId: string;
  journeySource: JourneySource;
  eventType: CareJourneyEventType;
  eventSetting: string | null;
  priority: CareJourneyPriority | null;
  dueDate: string;
  status: string;
  triggeringEncounterDateTime: string;
  clientProfile: ClientProfile;
  journeyType: string;
  currentStep: string;
  organizationId?: number | null;
  steps: CareJourneyStep[];
  attachments: CareJourneyAttachment[];
  logs: CareJourneyLog[];
  eventLogs: CareJourneyEventLog[];
  userAssignment: CareJourneyUserAssignment;
  orchestrationEngineDetails?: OrchestrationEngineDetails;
  cweDetails?: CweDetails;
  escalatedToCareTeam: boolean;
  escalationReason?: EscalationReason | null;
  escalatedBy?: EscalatedByUser | null;
  escalatedAt?: string | null;
  referralId?: string | null;
}

export enum EscalationReason {
  INVALID_OR_DISCONNECTED_PHONE_NUMBER = 'INVALID_OR_DISCONNECTED_PHONE_NUMBER',
  WRONG_NUMBER_OR_IDENTITY_MISMATCH = 'WRONG_NUMBER_OR_IDENTITY_MISMATCH',
  INCORRECT_PCP = 'INCORRECT_PCP',
  MEMBER_HANGS_UP_OR_REFUSES_INTERACTION = 'MEMBER_HANGS_UP_OR_REFUSES_INTERACTION',
  LANGUAGE_BARRIER_BEYOND_AI_CAPABILITY = 'LANGUAGE_BARRIER_BEYOND_AI_CAPABILITY',
  HEARING_SPEECH_IMPAIRMENTS = 'HEARING_SPEECH_IMPAIRMENTS',
  MEMBER_ASKS_CLINICAL_QUESTIONS_OR_HAS_CHALLENGES = 'MEMBER_ASKS_CLINICAL_QUESTIONS_OR_HAS_CHALLENGES',
  MEMBER_MENTIONS_BARRIER_TO_APPOINTMENT = 'MEMBER_MENTIONS_BARRIER_TO_APPOINTMENT',
  REQUEST_TO_SPEAK_WITH_KNOWN_PROVIDER_OR_COORDINATOR = 'REQUEST_TO_SPEAK_WITH_KNOWN_PROVIDER_OR_COORDINATOR',
  MEMBER_EXPRESSES_MEDICAL_EMERGENCY = 'MEMBER_EXPRESSES_MEDICAL_EMERGENCY',
  MEMBER_EXPRESSES_SUICIDAL_THOUGHTS = 'MEMBER_EXPRESSES_SUICIDAL_THOUGHTS',
  UNABLE_TO_SCHEDULE_IN_APPROVED_WINDOW = 'UNABLE_TO_SCHEDULE_IN_APPROVED_WINDOW',
}

export const escalationReasonDisplayValues: Record<EscalationReason, string> = {
  [EscalationReason.INVALID_OR_DISCONNECTED_PHONE_NUMBER]: 'Invalid or disconnected phone number',
  [EscalationReason.WRONG_NUMBER_OR_IDENTITY_MISMATCH]: 'Wrong number or identity mismatch',
  [EscalationReason.INCORRECT_PCP]: 'Incorrect PCP',
  [EscalationReason.MEMBER_HANGS_UP_OR_REFUSES_INTERACTION]: 'Member hangs up repeatedly or refuses interaction',
  [EscalationReason.LANGUAGE_BARRIER_BEYOND_AI_CAPABILITY]: 'Language barrier beyond AI capability',
  [EscalationReason.HEARING_SPEECH_IMPAIRMENTS]: 'Hearing/speech impairments affecting call quality',
  [EscalationReason.MEMBER_ASKS_CLINICAL_QUESTIONS_OR_HAS_CHALLENGES]: 'Member asks clinical questions or has challenges',
  [EscalationReason.MEMBER_MENTIONS_BARRIER_TO_APPOINTMENT]: 'Member mentions a barrier to attending the appointment',
  [EscalationReason.REQUEST_TO_SPEAK_WITH_KNOWN_PROVIDER_OR_COORDINATOR]:
    'Request to speak with a known provider or care coordinator',
  [EscalationReason.MEMBER_EXPRESSES_MEDICAL_EMERGENCY]: 'Member expresses that they are suffering from a medical emergency',
  [EscalationReason.MEMBER_EXPRESSES_SUICIDAL_THOUGHTS]: 'Member expresses suicidal thoughts',
  [EscalationReason.UNABLE_TO_SCHEDULE_IN_APPROVED_WINDOW]: 'Unable to schedule appointment in approved window',
};

// CARECO-5767/5768: reasons a navigator can give when rejecting a provider referral during
// pre-review. OTHER requires accompanying free text (rejectionReasonText).
export enum RejectionReason {
  CARE_MANAGEMENT_ELSEWHERE = 'CARE_MANAGEMENT_ELSEWHERE',
  DIAGNOSIS_OUT_OF_SCOPE = 'DIAGNOSIS_OUT_OF_SCOPE',
  DUPLICATE_CARE_JOURNEY = 'DUPLICATE_CARE_JOURNEY',
  PATIENT_OPTED_OUT = 'PATIENT_OPTED_OUT',
  INSUFFICIENT_INFORMATION = 'INSUFFICIENT_INFORMATION',
  OTHER = 'OTHER',
}

export const rejectionReasonDisplayValues: Record<RejectionReason, string> = {
  [RejectionReason.CARE_MANAGEMENT_ELSEWHERE]: 'Received care management elsewhere',
  [RejectionReason.DIAGNOSIS_OUT_OF_SCOPE]: 'Diagnosis out of scope',
  [RejectionReason.DUPLICATE_CARE_JOURNEY]: 'Duplicate Care Journey',
  [RejectionReason.PATIENT_OPTED_OUT]: 'Patient opted out of services',
  [RejectionReason.INSUFFICIENT_INFORMATION]: 'Bad Care Navigation Request / Not enough information',
  [RejectionReason.OTHER]: 'Other',
};

// Explicit order for the reject-reason dropdown (mirrors ESCALATION_REASONS in EscalationSection).
export const REJECTION_REASONS: RejectionReason[] = [
  RejectionReason.CARE_MANAGEMENT_ELSEWHERE,
  RejectionReason.DIAGNOSIS_OUT_OF_SCOPE,
  RejectionReason.DUPLICATE_CARE_JOURNEY,
  RejectionReason.PATIENT_OPTED_OUT,
  RejectionReason.INSUFFICIENT_INFORMATION,
  RejectionReason.OTHER,
];

export interface ScheduleAppointmentJourneyResponse extends CareJourneyDetailsResponse {
  careJourneyInstanceId: string;
  practiceName?: string;
  telephoneNumber?: string;
  practiceTelephone?: string;
  appointmentType: string;
  providerType: string;
  parentJourneyId?: string;
  encounterId?: string;
  providerName?: string;
}

export const ModuleType = {
  SEND_MESSAGE: 'SEND_MESSAGE',
  COMPLETE_FORM: 'COMPLETE_FORM',
  USER_TASK: 'USER_TASK',
};

/** userTaskType values for USER_TASK steps; selects the custom component in WorkflowStep. */
export const UserTaskType = {
  CARE_JOURNEY_PRE_REVIEW: 'CARE_JOURNEY_PRE_REVIEW',
} as const;

/** Address shape shared by referral provider/practice blobs. */
export interface CareJourneyReviewAddress {
  street1?: string;
  street2?: string;
  street3?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Referring provider / prescriber shape carried in the pre-review step context (JSONB blob). */
export interface CareJourneyReviewProvider {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  npiNumber?: string;
  type?: string;
  address?: CareJourneyReviewAddress;
}

/** Referring practice shape carried in the pre-review step context (JSONB blob). */
export interface CareJourneyReviewPractice {
  name?: string;
  phone?: string;
  email?: string;
  address?: CareJourneyReviewAddress;
}

/** Patient demographics group of the CARE_JOURNEY_PRE_REVIEW step context (CARECO-5758). */
export interface CareJourneyPreReviewPatient {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  phoneHome?: string;
  phoneMobile?: string;
  phoneWork?: string;
  patientCustomerProvidedId?: string;
}

/** Journey-level group of the CARE_JOURNEY_PRE_REVIEW step context (CARECO-5758). */
export interface CareJourneyPreReviewJourney {
  dataType?: string;
  referralSource?: string;
  pingsPatientId?: string;
  journeyInitiationMode?: JourneyInitiationMode;
  reasonForReferral?: string;
  referringProvider?: CareJourneyReviewProvider;
  prescriber?: CareJourneyReviewProvider;
  referralPractice?: CareJourneyReviewPractice;
}

/**
 * Structured prefill payload attached to the CARE_JOURNEY_PRE_REVIEW USER_TASK step by the
 * backend (careco-webapp CARECO-5758). Mirrors the fields the review component consumes; the
 * backend may send additional journey fields (insurance, clinical blobs) that this UI ignores.
 */
export interface CareJourneyPreReviewData {
  patient: CareJourneyPreReviewPatient;
  journey: CareJourneyPreReviewJourney;
  review?: CareJourneyPreReviewReview;
}

/**
 * Reviewer's Registry picker selections persisted on referral_review_data (CARECO-5762), carried
 * back so the pre-review pickers rehydrate what was saved. pcpPractitionerId is the thoth
 * practitioners.id key; pcpNpi is a nullable snapshot.
 */
export interface CareJourneyPreReviewReview {
  pcpPractitionerId?: string;
  pcpNpi?: string;
  pcpName?: string;
  practiceId?: string;
  practiceName?: string;
}

/**
 * Reviewer's confirmed working set posted to the pre-review Confirm endpoint (CARECO-5758):
 * POST /care_journeys/{id}/cwe/pre-review/confirm?roster_id={rosterId}. taskId is the Flowable
 * USER_TASK id to complete. PCP/practice fields are optional (PCP is optional per Decision #16).
 */
export interface ConfirmPreReviewRequest {
  taskId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  phoneHome?: string;
  phoneMobile?: string;
  phoneWork?: string;
  reasonForReferral?: string;
  pcpPractitionerId?: string;
  pcpNpi?: string;
  pcpName?: string;
  practiceId?: string;
  practiceName?: string;
  // CARECO-6285: when true, the backend clears the ingested referring_provider blob from
  // referral_review_data (hard removal, no undo).
  removeReferringProvider?: boolean;
}

/**
 * Reviewer's rejection posted to the pre-review Reject endpoint (CARECO-5767/5768):
 * POST /care_journeys/{id}/cwe/pre-review/reject?roster_id={rosterId}. The backend derives the
 * active pre-review USER_TASK server-side. rejectionReasonCode is a {@link RejectionReason};
 * rejectionReasonText is the free-text detail required only when the reason is OTHER.
 */
export interface RejectPreReviewRequest {
  rejectionReasonCode: string;
  rejectionReasonText?: string;
}

/**
 * Reviewer's editable working set for the pre-review draft-save endpoint (CARECO-5760):
 * PUT /care_journeys/{id}/cwe/pre-review/review-data?roster_id={rosterId}. Same fields as
 * {@link ConfirmPreReviewRequest} minus taskId — saving a draft persists edits without completing
 * the CARE_JOURNEY_PRE_REVIEW USER_TASK (the review stays pending).
 */
export interface ReviewDataEditRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  phoneHome?: string;
  phoneMobile?: string;
  phoneWork?: string;
  reasonForReferral?: string;
  pcpPractitionerId?: string;
  pcpNpi?: string;
  pcpName?: string;
  practiceId?: string;
  practiceName?: string;
  // CARECO-6285: clears the ingested referring_provider blob on referral_review_data when true.
  removeReferringProvider?: boolean;
}

export interface BaseCareJourneyStep {
  stepName: string;
  displayLabel?: string;
  assigneeType: string | null;
  status: string;
  statusReason?: string;
  surveyJson?: string;
  schemaGuid: string | null;
  attempts: Record<string, any>[];
  moduleType?: string;
  formKey?: string;
  taskId?: string;
  formData?: string;
  userTaskType?: string;
  reviewData?: CareJourneyPreReviewData;
  skippable?: boolean;
  skipped?: boolean;
}

export interface CareJourneyStep extends BaseCareJourneyStep{
  appointments?: ScheduleAppointmentJourneyResponse[];
}

export interface CareJourneyAttachment {
  id: string;
  s3Key?: string;
  type: CareJourneyAttachmentType;
  dmcsDocId?: string;
  createdBy?: User;
  createdAt: string;
  fax?: CareJourneyFax;
  fileName?: string;
}

export interface CareJourneyFax {
  fax?: string;
  externalId?: string;
  externalStatus?: CareJourneyFaxStatus;
  externalFailReason?: string;
  sentAt?: string;
  statusUpdatedAt?: string;
}

export interface CareJourneyLog {
  id: number;
  user?: User;
  eventDatetime: string;
  logType: CareJourneyLogType;
  systemOriginated: boolean;
  stepName: string;
  reason?: string;
  dueDate?: string;
  priority?: CareJourneyPriority;
}

export interface CareJourneyEventLog {
  messageId: string;
  messageBody: string;
  eventDatetime: string;
  eventType: string;
  stepName: string;
}

export interface CareJourneyRequest {
  priority: CareJourneyPriority | null;
  dueDate: string | null;
}

export interface CareJourneyUserAssignment {
  assignedToAi: boolean,
  users?: User[]
}


export interface Attestation {
  status: AttestationStatus;
  attestedBy?: {
    firstName: string;
    lastName: string;
    title?: string;
  };
  attestedAt?: string;
}

export interface DirectMessageDelivery {
  targetType: DirectMessageTargetType;
  directAddress: string;
  status: DirectMessageStatus;
  attachmentId: string
  failReason: string | null;
  deliveredAt: string | null;
  createdAt: string;
  initiatedBy?: BasicUser
}

export interface CareJourneyFilters {
  statuses?: string[],
  priorities?: string[],
  eventTypes?: string[],
  daysOpen?: string[],
  daysSinceDischarge?: string[],
  taskStartDate?: string | null,
  taskEndDate?: string | null,
  taskTypes?: string[],
  dueStartDate?: string | null,
  dueEndDate?: string | null,
  nextSteps?: string[],
  lastViewedStartDate?: string | null,
  lastViewedEndDate?: string | null,
  lastCompletedSteps?: string[],
  assignees?: string[],
  attestationStatuses?: string[],
  sortBy?: string,
  sortOrder?: string,
  searchName?: string | null,
  searchTaskId?: string | null,
  timeZone?: string;
  groups?: string[],
  rosterIds?: number[],
}

const DATE_RANGE_KEYS: Record<string, [string, string]> = {
  taskDate: ['taskStartDate', 'taskEndDate'],
  dueDate: ['dueStartDate', 'dueEndDate'],
  lastViewed: ['lastViewedStartDate', 'lastViewedEndDate'],
};

const formatDate = (d: any): string | null => {
  if (!d) return null;
  const parsed = new Date(d);
  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null;
};

export function formatTaskFiltersForApi(
  params: Record<string, any>,
): CareJourneyFilters {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (key in DATE_RANGE_KEYS) {
      const [startKey, endKey] = DATE_RANGE_KEYS[key];
      result[startKey] = formatDate(value?.startDate);
      result[endKey] = formatDate(value?.endDate);
    } else if (key === 'groups') {
      result['rosterIds'] = Array.isArray(value) && value.length > 0
        ? value.map((v: string) => Number(v)).filter((n: number) => !isNaN(n))
        : undefined;
    } else {
      result[key] = value;
    }
  }
  return result as CareJourneyFilters;
}

/**
 * True once the journey's engine-specific details have loaded. OE (TCM) journeys
 * expose `orchestrationEngineDetails`; CWE (FLOWABLE) journeys expose `cweDetails`.
 * Actions that work across both engines (e.g. Close) should gate on this rather
 * than on `orchestrationEngineDetails` alone, which is always absent for CWE.
 */
export function hasJourneyEngineDetails(journey: CareJourneyDetailsResponse): boolean {
  return Boolean(journey.orchestrationEngineDetails) || Boolean(journey.cweDetails);
}

export interface TaskSummaryRequest {
  pinnedFilters: { id: number; filters: CareJourneyFilters }[];
}

export interface TaskSummaryResponse {
  currentUserUuid: string;
  assignedTaskCount: number;
  overdueTaskCount: number;
  pinnedFilterCounts: { id: number; count: number }[];
}

export interface ScheduleAppointmentRequest {
  customerId: string;
  journeyType: string;
  parentJourneyId: string;
}

export interface ScheduleAppointmentAiRequest extends ScheduleAppointmentRequest{
  immediate?: boolean;
  providerOrganization: {
    name: string;
    phoneNumber?: string;
    address?: { street1?: string; city?: string; state?: string; zip?: string };
  };
  primaryCareProvider?: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    npi?: string;
  };
  appointmentType: string;
  providerType: string;
}

export interface ScheduleAppointmentHumanRequest extends ScheduleAppointmentRequest {
  // eslint-disable-next-line max-len
  // https://hbuco.atlassian.net/wiki/spaces/PE/pages/120348573707/Manual+AI+Appointment+Scheduling+API+Integration+Spec#4.1-Confirmation-channel-%E2%80%94-channel_selection-vs-force_hitl
  confirmationChannel: 'channel_selection' | 'force_hitl';
  appointmentType: string,
  providerType: string,
  appointmentDateTime: string;
  appointmentSetting: string;
  scheduledWith: string;
  location: string;
  form: Record<string, any>;
}

export interface ScheduleAppointmentResponse {
  journey_id: string;
  appointmentRequestId: string;
  message: string;
}

export interface SealAppointmentRequest {
  journey_id: string;
  sealed: boolean;
  sealed_by: string;
  reason?: string;
}

export interface CancelAppointmentRequest {
  journey_id: string;
  disposition: 'Cancelled' | 'Closed';
  canceledBy: string;
  reason: string;
}
